import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { BsCalendar3, BsChevronDown } from 'react-icons/bs'
import { useDebounce } from 'use-debounce'
import { format, differenceInDays } from 'date-fns'
import {
  Handshake,
  MailOpen,
  NotepadText,
  HandCoins,
  Layers,
  LayoutList,
  FileText,
} from 'lucide-react'
import { PiCalendarStar } from 'react-icons/pi'
import { CompanyPageAccordion } from '../CompanyPageAccordion'
import { ResponsiveTabs, ResponsiveTabConfig } from '../../Tabs'
import { EventHistoryList } from './EventHistoryList'
import { groupEventsByDate } from './utils'
import useApiService from 'services/api'
import { Popover, PopoverContent, PopoverTrigger } from '../../UI/popover'
import { Calendar } from '../../UI/calendar'
import { SampleRequestPreviewModal } from '../../Samples/SampleRequestPreviewModal'
import { EventHistoryFilterModal } from './EventHistoryFilterModal'
import { useEventHistoryFilterStore } from './EventHistoryFilterStore'
import { EventHistoryFilterChips } from './EventHistoryFilterChips'
import { EmailsModal } from '../Modals/EmailsModal/EmailsModal'
import { ActivityV2 } from 'models/activityV2'
import { EmailMessage } from 'models/emailIntegration'

export type EventHistoryTab =
  | 'all'
  | 'deals'
  | 'notes'
  | 'reminders'
  | 'samples'
  | 'emails'
  | 'documents'
  | 'account_attributes'

// Wrapper to convert react-icons to lucide-style component
const CalendarStarIcon = ({
  size,
  className,
}: {
  size?: number
  className?: string
}) => <PiCalendarStar size={size} className={className} />

const EVENT_HISTORY_TABS: ResponsiveTabConfig<EventHistoryTab>[] = [
  { key: 'all', title: 'All', icon: LayoutList },
  { key: 'deals', title: 'Deals', icon: Handshake },
  { key: 'notes', title: 'Notes', icon: NotepadText },
  { key: 'reminders', title: 'Reminders', icon: CalendarStarIcon },
  { key: 'samples', title: 'Samples', icon: HandCoins },
  { key: 'emails', title: 'Emails', icon: MailOpen },
  { key: 'documents', title: 'Documents', icon: FileText },
  { key: 'account_attributes', title: 'Account Attributes', icon: Layers },
]

interface EventHistoryAccordionProps {
  chainProxyId: number
  doorProxyId?: number
  companyType?: string
}

export function EventHistoryAccordion({
  chainProxyId,
  doorProxyId,
}: EventHistoryAccordionProps) {
  const api = useApiService()
  const [selectedTab, setSelectedTab] = useState<EventHistoryTab>('all')
  const searchTerm = ''
  // const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const [dateRange, setDateRange] = useState<
    | {
        from: Date
        to?: Date
      }
    | undefined
  >(undefined)

  // Get filters from store
  const { eventTypes, teamMemberIds, criteria } = useEventHistoryFilterStore()

  // Clear event type filters when switching to a specific tab (not "all")
  useEffect(() => {
    if (selectedTab !== 'all' && eventTypes.length > 0) {
      useEventHistoryFilterStore.getState().setPendingEventTypes([])
      useEventHistoryFilterStore.getState().applyFilters()
    }
  }, [selectedTab, eventTypes.length])

  // Sample preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewingSampleId, setPreviewingSampleId] = useState<number | null>(
    null
  )

  // Sample preview modal handlers
  const handlePreviewRequest = (sampleId: number) => {
    setPreviewingSampleId(sampleId)
    setShowPreviewModal(true)
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setPreviewingSampleId(null)
  }

  // Email view modal state
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  // Email view modal handler
  const handleEmailClick = (event: ActivityV2) => {
    // Get object_id from first field_history_object
    const objectId = event.field_history_objects?.[0]?.object_id || event.id

    // Construct EmailMessage from event data
    const emailData: EmailMessage = {
      id: objectId,
      outbound: false, // Will be determined from field_history_objects
      related_contacts: [],
      sender: null, // Will be loaded from API
      to: null, // Will be loaded from API
      cc: null, // Will be loaded from API
      subject: '',
      snippet: '',
      date: event.created_at,
    }

    // Extract data from field_history_objects
    event.field_history_objects?.forEach((field) => {
      if (field.field_name === 'subject') {
        emailData.subject = field.value || ''
      } else if (field.field_name === 'snippet') {
        emailData.snippet = field.value || ''
      } else if (field.field_name === 'outbound') {
        emailData.outbound = field.value || false
      }
    })

    setSelectedEmail(emailData)
  }

  const handleCloseEmailModal = () => {
    setSelectedEmail(null)
  }

  const PAGE_SIZE = 20

  // Build query params for each page
  const buildQueryParams = (pageParam: number) => {
    const baseParams: Record<string, string | number | string[] | number[]> = {
      page_size: PAGE_SIZE,
      page: pageParam,
    }

    // Add door proxy filter if on door page
    if (doorProxyId) {
      baseParams.door_proxy_id = doorProxyId
    }

    // Add search parameter if search term exists
    if (debouncedSearchTerm.trim()) {
      baseParams.search = debouncedSearchTerm.trim()
    }

    // Add event_type filter - prioritize "More Filters" over tab selection
    if (eventTypes.length > 0) {
      // Use filters from "More Filters" modal - backend supports multiple values
      baseParams.event_type = eventTypes
    } else if (selectedTab !== 'all') {
      // Fall back to tab-based filtering
      const eventTypeMap: Record<EventHistoryTab, string> = {
        all: '', // not used
        deals: 'deal',
        notes: 'notes', // Backend maps this to companynote, contactnote, doornote
        reminders: 'reminder',
        samples: 'samples', // Backend maps this to samplerequest
        emails: 'email', // Backend maps this to emailmessage
        documents: 'document',
        account_attributes: 'contactcompany',
      }
      baseParams.event_type = eventTypeMap[selectedTab]
    }

    // Add team member filter - backend supports multiple values
    if (teamMemberIds.length > 0) {
      baseParams.user = teamMemberIds
    }

    // Add filter criteria (all, one, neither) - always include when filters are active
    // This ensures changing criteria triggers a new API call
    if (eventTypes.length > 0 || teamMemberIds.length > 0) {
      baseParams.filter_type = criteria
    }

    // Add date range filters
    if (dateRange?.from) {
      baseParams.created_at_greater = dateRange.from.toISOString()
    }
    if (dateRange?.to) {
      baseParams.created_at_less = dateRange.to.toISOString()
    }

    return baseParams
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: [
      'companyActivity',
      chainProxyId,
      doorProxyId,
      selectedTab,
      debouncedSearchTerm,
      dateRange,
      eventTypes,
      teamMemberIds,
      criteria,
    ],
    queryFn: ({ pageParam }) =>
      api.getCompanyActivityList(chainProxyId, buildQueryParams(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      if (totalFetched < lastPage.count) {
        return allPages.length + 1
      }
      return undefined
    },
    enabled: !!chainProxyId,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  // Flatten all pages of events into a single array and group by date
  const groupedEvents = useMemo(() => {
    if (!data?.pages) return []
    const allEvents = data.pages.flatMap((page) => page.results)

    // Filter out unwanted events
    const filteredEvents = allEvents.filter((event) => {
      // Filter out document events with empty field_history_objects
      // These are internal versioning events (is_deleted, deleted_at, etc)
      if (event.content_type?.model === 'document') {
        return (
          event.field_history_objects && event.field_history_objects.length > 0
        )
      }
      // Filter out email UPDATE events - we only want to show email CREATE events (new emails)
      if (
        event.content_type?.model === 'emailmessage' &&
        event.action_type === 'UPDATE'
      ) {
        return false
      }
      return true
    })

    return groupEventsByDate(filteredEvents)
  }, [data?.pages])

  // Get the most recent event for the header
  const mostRecentEvent = useMemo(() => {
    if (!data?.pages?.[0]?.results?.[0]) return null
    return data.pages[0].results[0]
  }, [data?.pages])

  // Calculate days since last update
  const daysSinceUpdate = useMemo(() => {
    if (!mostRecentEvent?.created_at) return 0
    return differenceInDays(new Date(), new Date(mostRecentEvent.created_at))
  }, [mostRecentEvent])

  // Get color for the days badge based on thresholds
  const getDaysBadgeColor = (days: number) => {
    if (days < 30) return '#57D36F' // Green
    if (days < 90) return '#FAB661' // Orange
    return '#EA4056' // Red
  }

  // Header content with last updated info
  const headerRight = mostRecentEvent ? (
    <>
      {/* Last Updated Date */}
      <div className="flex items-center gap-1">
        <BsCalendar3 className="w-4 h-4 text-fg-secondary" />
        <span
          className="text-xxs font-light text-fg"
          style={{ lineHeight: '18px' }}
        >
          Updated {format(new Date(mostRecentEvent.created_at), 'MM/dd/yyyy')}
        </span>
      </div>

      {/* Days Badge */}
      <div className="flex h-5 items-center gap-1 rounded-full border border-line bg-surface-raised px-1.5 py-0.5">
        <div
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: getDaysBadgeColor(daysSinceUpdate) }}
        />
        <span
          className="text-xxs font-light text-fg"
          style={{ lineHeight: '100%' }}
        >
          <span className="font-semibold">{daysSinceUpdate}</span>{' '}
          {daysSinceUpdate === 1 ? 'day' : 'days'}
        </span>
      </div>
    </>
  ) : null

  return (
    <CompanyPageAccordion
      title="Event History"
      defaultOpen={true}
      stickyHeader={true}
      headerRight={headerRight}
      noPaddingBottom={true}
    >
      {/* Tabs and Search - static header */}
      <div className="bg-gray-50 space-y-4 pb-4">
        {/* Tabs */}
        <ResponsiveTabs
          tabs={EVENT_HISTORY_TABS}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          className="border-b border-gray-200"
        />

        {/* Search Bar */}
        {/* <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <BsSearch className="w-4 h-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search for an event"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fb-dark-green focus:border-fb-dark-green outline-none"
          />
        </div> */}

        {/* Filters Row */}
        <div className="w-full flex gap-2">
          {/* Date Range Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex-1 flex items-center gap-2 px-3.5 py-2 bg-white border border-line rounded-md text-sm text-fg hover:border-fb-dark-green transition-colors">
                <BsCalendar3 className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left">
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, 'd MMM yyyy')} -{' '}
                        {format(dateRange.to, 'd MMM yyyy')}
                      </>
                    ) : (
                      format(dateRange.from, 'd MMM yyyy')
                    )
                  ) : (
                    'All time'
                  )}
                </span>
                <BsChevronDown className="w-5 h-5 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={(newRange) => {
                  if (!newRange?.from) {
                    return
                  }
                  setDateRange(newRange as { from: Date; to?: Date })
                }}
                numberOfMonths={2}
              />
              {dateRange && (
                <div className="p-3 border-t">
                  <button
                    onClick={() => setDateRange(undefined)}
                    className="w-full px-3 py-1.5 text-sm text-gray-600 hover:text-fb-dark-green transition-colors"
                  >
                    Clear dates
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* More Filters Button */}
          <EventHistoryFilterModal selectedTab={selectedTab} />
        </div>

        {/* Filter Chips */}
        <EventHistoryFilterChips selectedTab={selectedTab} />
      </div>

      <EventHistoryList
        isLoading={isLoading}
        error={error}
        groupedEvents={groupedEvents}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        onPreviewRequest={handlePreviewRequest}
        onEmailClick={handleEmailClick}
      />

      {/* Sample Preview Modal */}
      {showPreviewModal && (
        <SampleRequestPreviewModal
          show={showPreviewModal}
          onHide={handleClosePreviewModal}
          sampleRequestId={previewingSampleId}
        />
      )}

      {/* Email View Modal */}
      {selectedEmail && (
        <EmailsModal
          show={!!selectedEmail}
          onHide={handleCloseEmailModal}
          emailOnlyMode
          initialEmail={selectedEmail}
        />
      )}
    </CompanyPageAccordion>
  )
}
